// MF-67 — Upload images or files for a Journal entry (image / attachment fields and the entry's own
// attachments). Uses the app's presigned-URL flow (POST upload/presign-url → PUT to S3), stored under
// the tenant's `journal/` folder; the BE allows images, video, audio, PDF, Office, text, CSV and
// archives up to 5 MB each. Value: [{ id, name, url, type, size }].
// Removing an item only detaches it from the entry (the stored file is kept, so history can't break).
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import globalConfig from '../../../../config/globalConfig'
import { presigneUrl, uploadFile } from '../../../../services/upload/upload.service'

const FOLDER = 'journal'
const MAX_BYTES = 5 * 1000 * 1000

const fmtSize = (b = 0) =>
  b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`
export const isImage = (f) => String(f?.type || '').startsWith('image/')

const FileUpload = ({
  value = [],
  onChange,
  kind = 'file',
  multiple = true,
  disabled = false,
  disabledText
}) => {
  const tenant = useSelector((state) => state?.tenant?.tenant ?? {})
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState([])
  const images = kind === 'image'
  const files = Array.isArray(value) ? value : []

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || [])
    e.target.value = ''
    if (!picked.length) return
    const tooBig = picked.filter((f) => f.size > MAX_BYTES)
    const ok = picked.filter((f) => f.size <= MAX_BYTES && (!images || isImage(f)))
    const errs = [
      ...tooBig.map((f) => `${f.name}: larger than 5 MB`),
      ...(images ? picked.filter((f) => !isImage(f)).map((f) => `${f.name}: not an image`) : [])
    ]
    if (!ok.length) {
      setErrors(errs)
      return
    }
    setBusy(true)
    try {
      const res = await presigneUrl(
        ok.map((f) => ({ name: f.name, type: f.type, size: f.size })),
        FOLDER
      )
      const added = []
      for (const item of res?.data || []) {
        if (!item?.data) {
          errs.push(`${item?.error?.file || 'File'}: ${item?.error?.msg || 'not allowed'}`)
          continue
        }
        const src = ok.find((f) => f.name === item.data.file?.name)
        if (!src) continue
        try {
          await uploadFile(item.data.presignedUrl, src)
          added.push({
            id: item.data.filename,
            name: src.name,
            type: src.type,
            size: src.size,
            url: `${globalConfig.storage || window.location.origin}/tenants/${tenant?.tenantId}/${FOLDER}/${item.data.filename}`
          })
        } catch {
          errs.push(`${src.name}: upload failed`)
        }
      }
      if (added.length) onChange(multiple ? [...files, ...added] : added.slice(0, 1))
    } catch {
      errs.push('Could not start the upload. Try again.')
    } finally {
      setErrors(errs)
      setBusy(false)
    }
  }

  const remove = (id) => onChange(files.filter((f) => f.id !== id))

  if (disabled) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 10,
          border: '1px dashed rgb(203, 213, 225)',
          color: 'rgb(148, 163, 184)',
          fontSize: 13,
          textAlign: 'center'
        }}
      >
        {disabledText || (images ? 'Staff will upload images here.' : 'Staff will attach files here.')}
      </div>
    )
  }

  return (
    <div className='flex flex-col' style={{ gap: 10 }}>
      {files.length ? (
        images ? (
          <div className='flex flex-wrap' style={{ gap: 10 }}>
            {files.map((f) => (
              <div
                key={f.id}
                style={{
                  position: 'relative',
                  width: 112,
                  height: 112,
                  borderRadius: 10,
                  overflow: 'hidden',
                  border: '1px solid rgb(226, 232, 240)',
                  background: 'rgb(248, 250, 252)'
                }}
              >
                <a href={f.url} target='_blank' rel='noreferrer' title={f.name}>
                  <img
                    src={f.url}
                    alt={f.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </a>
                <Tooltip arrow title='Remove'>
                  <IconButton
                    aria-label={`Remove ${f.name}`}
                    size='small'
                    onClick={() => remove(f.id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 26,
                      height: 26,
                      background: 'rgba(255,255,255,0.9)'
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'xmark']} style={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        ) : (
          <div className='flex flex-col' style={{ gap: 6 }}>
            {files.map((f) => (
              <div
                key={f.id}
                className='flex items-center'
                style={{
                  gap: 10,
                  padding: '8px 8px 8px 12px',
                  borderRadius: 10,
                  border: '1px solid rgb(226, 232, 240)',
                  background: '#fff'
                }}
              >
                <FontAwesomeIcon
                  icon={['fal', isImage(f) ? 'file-image' : 'file-alt']}
                  style={{ fontSize: 16, color: 'rgb(100, 116, 139)' }}
                />
                <a
                  href={f.url}
                  target='_blank'
                  rel='noreferrer'
                  className='grow'
                  style={{
                    fontSize: 13,
                    color: 'rgb(37, 99, 235)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {f.name}
                </a>
                <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>{fmtSize(f.size)}</span>
                <Tooltip arrow title='Remove'>
                  <IconButton aria-label={`Remove ${f.name}`} size='small' onClick={() => remove(f.id)}>
                    <FontAwesomeIcon icon={['fal', 'trash-alt']} style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        )
      ) : null}

      {multiple || !files.length ? (
        <div>
          <input
            ref={inputRef}
            type='file'
            hidden
            multiple={multiple}
            accept={images ? 'image/*' : undefined}
            onChange={onPick}
          />
          <Button
            variant='outlined'
            size='small'
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            startIcon={
              busy ? (
                <CircularProgress size={12} />
              ) : (
                <FontAwesomeIcon icon={['fal', images ? 'image' : 'paperclip']} style={{ fontSize: 13 }} />
              )
            }
          >
            {busy
              ? 'Uploading…'
              : images
                ? files.length
                  ? 'Add image'
                  : 'Upload image'
                : files.length
                  ? 'Attach another file'
                  : 'Attach file'}
          </Button>
          <Typography component='span' style={{ fontSize: 12, color: 'rgb(100, 116, 139)', marginLeft: 10 }}>
            {images ? 'JPG, PNG, GIF…' : 'Images, PDF, Office, text…'} up to 5 MB
          </Typography>
        </div>
      ) : null}

      {errors.length ? (
        <div className='flex flex-col' style={{ gap: 2 }}>
          {errors.map((m) => (
            <span key={m} style={{ fontSize: 12, color: 'rgb(220, 38, 38)' }}>
              {m}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default FileUpload
